import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "./CSS/InformeInventario.css";
import { dominio } from "../../../routes_backend/routes.jsx";

const API_ENCABEZADOS = `${dominio}/getInformeInventario`;
const API_DETALLE = `${dominio}/getInformeInventarioDetalle`;

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

const fmtDate = (iso) => {
  if (!iso) return "Sin fecha";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return safeStr(iso);
  return d.toLocaleString("es-GT");
};

const fmtDateOnly = (iso) => {
  if (!iso) return "Sin fecha";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return safeStr(iso);

  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const anio = d.getFullYear();

  return `${dia}/${mes}/${anio}`;
};

const getEstadoLabel = (estado) => {
  const value = safeStr(estado).trim().toUpperCase();
  if (value === "C") return "Cerrado";
  if (value === "D") return "Borrador";
  return value || "N/D";
};

const getEstadoBadgeClass = (estado) => {
  const value = safeStr(estado).trim().toUpperCase();
  if (value === "C") return "estado-badge estado-g";
  if (value === "D") return "estado-badge estado-p";
  return "estado-badge estado-nd";
};

const getCurrentUsernameFromToken = () => {
  try {
    let username = "";
    const token = localStorage.getItem("token");
    const tokenData = JSON.parse(token);

    if (tokenData !== null && Array.isArray(tokenData)) {
      tokenData.forEach((element) => {
        username = element.usuarioSAP;
      });
    }

    return username || "N/D";
  } catch (error) {
    console.error("Error leyendo token:", error);
    return "N/D";
  }
};

const toComparableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(/,/g, "").trim();
  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};

const getWarehouseMatchMap = (line) => {
  const conteo = toComparableNumber(line?.Conteo);

  const warehouses = {
    stock_BCM01: toComparableNumber(line?.stock_BCM01),
    stock_BCM02: toComparableNumber(line?.stock_BCM02),
    stock_BCM03: toComparableNumber(line?.stock_BCM03),
    stock_BCM04: toComparableNumber(line?.stock_BCM04),
    stock_BPU01: toComparableNumber(line?.stock_BPU01),
  };

  const hasValidConteo = conteo !== null;
  const matches = {};

  Object.entries(warehouses).forEach(([key, value]) => {
    matches[key] = hasValidConteo && value !== null && value === conteo;
  });

  const hasAnyMatch = Object.values(matches).some(Boolean);

  return {
    conteo,
    hasAnyMatch,
    matches,
  };
};

const getCellHighlightClass = (line, field) => {
  const { conteo, hasAnyMatch, matches } = getWarehouseMatchMap(line);

  if (conteo === null) return "";

  const warehouseFields = [
    "stock_BCM01",
    "stock_BCM02",
    "stock_BCM03",
    "stock_BCM04",
    "stock_BPU01",
  ];

  if (hasAnyMatch) {
    if (field === "Codigo" || field === "Conteo") return "cell-match";
    if (warehouseFields.includes(field) && matches[field]) return "cell-match";
    return "";
  }

  if (field === "Codigo" || field === "Conteo" || warehouseFields.includes(field)) {
    return "cell-no-match";
  }

  return "";
};

const DEFAULT_COLUMN_WIDTHS = {
  Documento: 110,
  Codigo: 120,
  Descripcion: 260,
  Tag: 120,
  Estante: 100,
  Seccion: 100,
  Nivel: 90,
  Conteo: 95,
  stock_BCM01: 110,
  stock_BCM02: 110,
  stock_BCM03: 110,
  stock_BCM04: 110,
  stock_BPU01: 110,
};

const columns = [
  { key: "Documento", label: "Documento" },
  { key: "Codigo", label: "Código" },
  { key: "Descripcion", label: "Descripción" },
  { key: "Tag", label: "Tag" },
  { key: "Estante", label: "Estante" },
  { key: "Seccion", label: "Sección" },
  { key: "Nivel", label: "Nivel" },
  { key: "Conteo", label: "Conteo" },
  { key: "stock_BCM01", label: "BCM01" },
  { key: "stock_BCM02", label: "BCM02" },
  { key: "stock_BCM03", label: "BCM03" },
  { key: "stock_BCM04", label: "BCM04" },
  { key: "stock_BPU01", label: "BPU01" },
];

const InformeInventario = () => {
  const [encabezados, setEncabezados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleError, setDetalleError] = useState(null);

  const [detalleDoc, setDetalleDoc] = useState(null);
  const [detalleDocs, setDetalleDocs] = useState([]);
  const [isConsolidated, setIsConsolidated] = useState(false);

  const [selectedDocs, setSelectedDocs] = useState([]);

  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  const resizeStateRef = useRef(null);

  const currentUsername = getCurrentUsernameFromToken();

  const fetchEncabezados = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(API_ENCABEZADOS, {
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Error ${res.status}: ${txt}`);
      }

      const data = await res.json();
      setEncabezados(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al obtener encabezados de inventario");
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleDetalle = async (idDocInventario) => {
    const res = await fetch(
      `${API_DETALLE}?idDocInventario=${encodeURIComponent(idDocInventario)}`,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Error ${res.status}: ${txt}`);
    }

    return res.json();
  };

  const fetchDetalle = async (idDocInventario) => {
    try {
      setDetalleLoading(true);
      setDetalleError(null);
      setDetalleDoc(null);
      setDetalleDocs([]);
      setIsConsolidated(false);
      setDetalleOpen(true);

      const data = await fetchSingleDetalle(idDocInventario);
      setDetalleDoc(data);
    } catch (err) {
      console.error(err);
      setDetalleError(err.message || "Error al obtener detalle del inventario");
    } finally {
      setDetalleLoading(false);
    }
  };

  const fetchDetalleConsolidado = async () => {
    if (selectedDocs.length === 0) return;

    try {
      setDetalleLoading(true);
      setDetalleError(null);
      setDetalleDoc(null);
      setDetalleDocs([]);
      setIsConsolidated(true);
      setDetalleOpen(true);

      const docs = await Promise.all(
        selectedDocs.map((id) => fetchSingleDetalle(id))
      );

      setDetalleDocs(docs);
    } catch (err) {
      console.error(err);
      setDetalleError(err.message || "Error al consolidar inventarios");
    } finally {
      setDetalleLoading(false);
    }
  };

  const toggleSelectDoc = (idDocInventario) => {
    setSelectedDocs((prev) => {
      const id = String(idDocInventario);
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const clearSelection = () => {
    setSelectedDocs([]);
  };

  const descargarExcelDetalle = () => {
    const docs = isConsolidated
      ? detalleDocs
      : detalleDoc
      ? [detalleDoc]
      : [];

    if (docs.length === 0) return;

    const resumen = docs.map((doc) => ({
      idDocInventario: safeStr(doc.idDocInventario),
      FechaCreacion: fmtDateOnly(doc.FechaCreacion),
      FechaDocumento: fmtDateOnly(doc.FechaDocumento),
      FechaActualizacion: safeStr(doc.FechaActualizacion),
      UsuarioActual: currentUsername,
      Estado: getEstadoLabel(doc.Estado),
      Comentario: safeStr(doc.Comentario),
      TotalLineas: (doc.DocumentLines || []).length,
    }));

    const lineas = docs.flatMap((doc) =>
      (doc.DocumentLines || []).map((line) => ({
        Documento: safeStr(doc.idDocInventario),
        Codigo: safeStr(line.Codigo),
        Descripcion: safeStr(line.Descripcion),
        Tag: safeStr(line.Tag),
        Estante: safeStr(line.Estante),
        Seccion: safeStr(line.Seccion),
        Nivel: safeStr(line.Nivel),
        Conteo: safeStr(line.Conteo),
        BCM01: safeStr(line.stock_BCM01),
        BCM02: safeStr(line.stock_BCM02),
        BCM03: safeStr(line.stock_BCM03),
        BCM04: safeStr(line.stock_BCM04),
        BPU01: safeStr(line.stock_BPU01),
      }))
    );

    const wb = XLSX.utils.book_new();
    const wsResumen = XLSX.utils.json_to_sheet(resumen);
    const wsDetalle = XLSX.utils.json_to_sheet(lineas);

    XLSX.utils.book_append_sheet(
      wb,
      wsResumen,
      isConsolidated ? "Inventarios" : "Encabezado"
    );
    XLSX.utils.book_append_sheet(wb, wsDetalle, "Detalle");

    XLSX.writeFile(
      wb,
      isConsolidated
        ? `Consolidado_Inventarios_${docs.length}_documentos.xlsx`
        : `InformeInventario_${safeStr(docs[0]?.idDocInventario) || "Documento"}.xlsx`
    );
  };

  const closeDetalleModal = () => {
    setDetalleOpen(false);
    setDetalleDoc(null);
    setDetalleDocs([]);
    setDetalleError(null);
    setIsConsolidated(false);
  };

  useEffect(() => {
    fetchEncabezados();
  }, []);

  useEffect(() => {
    if (!detalleOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDetalleModal();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [detalleOpen]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const state = resizeStateRef.current;
      if (!state) return;

      const delta = e.clientX - state.startX;
      const nextWidth = Math.max(70, state.startWidth + delta);

      setColumnWidths((prev) => ({
        ...prev,
        [state.columnKey]: nextWidth,
      }));
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      document.body.classList.remove("col-resize-active");
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startResize = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();

    resizeStateRef.current = {
      columnKey,
      startX: e.clientX,
      startWidth: columnWidths[columnKey] || DEFAULT_COLUMN_WIDTHS[columnKey] || 120,
    };

    document.body.classList.add("col-resize-active");
  };

  const filtered = useMemo(() => {
    if (!search) return encabezados;

    const q = search.toLowerCase().trim();

    return encabezados.filter((item) => {
      const idDoc = safeStr(item.idDocInventario).toLowerCase();
      const comentario = safeStr(item.Comentario).toLowerCase();

      return idDoc.includes(q) || comentario.includes(q);
    });
  }, [encabezados, search]);

  const modalDocs = isConsolidated
    ? detalleDocs
    : detalleDoc
    ? [detalleDoc]
    : [];

  const modalLines = modalDocs.flatMap((doc) =>
    (doc.DocumentLines || []).map((line) => ({
      ...line,
      Documento: doc.idDocInventario,
    }))
  );

  return (
    <div className="solicitud-traslado-container">
      <header className="solicitud-traslado-header">
        <div>
          <h1>Informe de Inventario</h1>
          <p>Listado de encabezados de inventario</p>
        </div>

        <div className="solicitud-traslado-actions">
          <input
            type="text"
            placeholder="Filtrar por idDocInventario o comentario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="inventario-bulk-actions">
            <button
              type="button"
              className="inventario-btn inventario-btn-primary"
              onClick={fetchDetalleConsolidado}
              disabled={selectedDocs.length === 0}
            >
              Consolidar inventarios ({selectedDocs.length})
            </button>

            <button
              type="button"
              className="inventario-btn inventario-btn-light"
              onClick={clearSelection}
              disabled={selectedDocs.length === 0}
            >
              Limpiar selección
            </button>
          </div>
        </div>
      </header>

      {loading && (
        <div className="solicitud-traslado-status status-loading">
          Cargando encabezados...
        </div>
      )}

      {error && (
        <div className="solicitud-traslado-status status-error">
          Ocurrió un error: {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="solicitud-traslado-status status-empty">
          No hay registros que coincidan con el filtro.
        </div>
      )}

      <section className="solicitud-traslado-list">
        {filtered.map((item) => {
          const id = safeStr(item.idDocInventario);
          const estadoTexto = getEstadoLabel(item.Estado);
          const estadoClase = getEstadoBadgeClass(item.Estado);
          const isSelected = selectedDocs.includes(id);

          return (
            <article
              key={id}
              className={`solicitud-card ${isSelected ? "solicitud-card-selected" : ""}`}
            >
              <div className="solicitud-card-select">
                <label className="inventario-check-label">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelectDoc(id)}
                  />
                  <span>Seleccionar</span>
                </label>
              </div>

              <div className="solicitud-card-header">
                <div className="solicitud-card-main">
                  <h2>Inventario #{safeStr(item.idDocInventario) || "N/D"}</h2>

                  <p className="solicitud-card-subtitle">
                    Usuario actual: <strong>{currentUsername}</strong>
                  </p>

                  <p className="solicitud-card-comments">
                    Comentario: <strong>{safeStr(item.Comentario) || "Sin comentario"}</strong>
                  </p>

                  <p className="solicitud-card-comments">
                    Fecha documento: <strong>{fmtDateOnly(item.FechaDocumento)}</strong>
                    {" · "}
                    Fecha creación: <strong>{fmtDateOnly(item.FechaCreacion)}</strong>
                  </p>

                  <p className="solicitud-card-comments">
                    Fecha actualización: <strong>{fmtDate(item.FechaActualizacion)}</strong>
                  </p>
                </div>

                <div className="solicitud-card-meta">
                  <span className={estadoClase} title={`Estado: ${estadoTexto}`}>
                    {estadoTexto}
                  </span>

                  <span className="solicitud-date">
                    ID: <strong>{safeStr(item.idDocInventario) || "N/D"}</strong>
                  </span>

                  <button
                    type="button"
                    className="solicitud-transfer-btn"
                    onClick={() => fetchDetalle(item.idDocInventario)}
                  >
                    Ver detalle
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {detalleOpen && (
        <div
          className="inventario-modal-overlay"
          onClick={closeDetalleModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="inventario-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventario-modal-header">
              <div>
                <h3 className="inventario-modal-title">
                  {isConsolidated
                    ? `Consolidado de inventarios (${modalDocs.length})`
                    : `Detalle inventario${detalleDoc?.idDocInventario ? ` #${detalleDoc.idDocInventario}` : ""}`}
                </h3>
                <p className="inventario-modal-subtitle">
                  {isConsolidated
                    ? "Visualización consolidada de los inventarios seleccionados"
                    : "Visualización del encabezado y sus líneas de inventario"}
                </p>
              </div>

              <div className="inventario-modal-actions">
                <button
                  type="button"
                  className="inventario-btn inventario-btn-success"
                  onClick={descargarExcelDetalle}
                  disabled={modalDocs.length === 0 || detalleLoading}
                >
                  Descargar Excel
                </button>

                <button
                  type="button"
                  className="inventario-btn inventario-btn-secondary"
                  onClick={closeDetalleModal}
                >
                  Cerrar
                </button>
              </div>
            </div>

            <div className="inventario-modal-body">
              {detalleLoading && (
                <div className="inventario-modal-status">Cargando detalle...</div>
              )}

              {detalleError && (
                <div className="inventario-modal-status inventario-modal-status-error">
                  Ocurrió un error: {detalleError}
                </div>
              )}

              {!detalleLoading && !detalleError && modalDocs.length > 0 && (
                <>
                  <div className="inventario-resumen-grid">
                    {isConsolidated ? (
                      <>
                        <div className="inventario-resumen-item">
                          <span>Inventarios seleccionados</span>
                          <strong>{modalDocs.length}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Total líneas</span>
                          <strong>{modalLines.length}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Usuario actual</span>
                          <strong>{currentUsername}</strong>
                        </div>

                        <div className="inventario-resumen-item inventario-resumen-item-wide">
                          <span>Documentos</span>
                          <strong>
                            {modalDocs.map((doc) => safeStr(doc.idDocInventario)).join(", ")}
                          </strong>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="inventario-resumen-item">
                          <span>ID Documento</span>
                          <strong>{safeStr(detalleDoc.idDocInventario)}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Usuario actual</span>
                          <strong>{currentUsername}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Estado</span>
                          <strong>{getEstadoLabel(detalleDoc.Estado)}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Fecha Documento</span>
                          <strong>{fmtDateOnly(detalleDoc.FechaDocumento)}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Fecha Creación</span>
                          <strong>{fmtDateOnly(detalleDoc.FechaCreacion)}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Fecha Actualización</span>
                          <strong>{fmtDate(detalleDoc.FechaActualizacion)}</strong>
                        </div>

                        <div className="inventario-resumen-item">
                          <span>Total Líneas</span>
                          <strong>{(detalleDoc.DocumentLines || []).length}</strong>
                        </div>

                        <div className="inventario-resumen-item inventario-resumen-item-wide">
                          <span>Comentario</span>
                          <strong>{safeStr(detalleDoc.Comentario) || "Sin comentario"}</strong>
                        </div>
                      </>
                    )}
                  </div>

                  {isConsolidated && (
                    <div className="inventario-selected-docs">
                      {modalDocs.map((doc) => (
                        <span key={safeStr(doc.idDocInventario)} className="inventario-selected-badge">
                          #{safeStr(doc.idDocInventario)}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="inventario-table-hint">
                    Desliza horizontalmente para ver todas las columnas y arrastra el borde del encabezado para cambiar el ancho.
                  </div>

                  <div className="inventario-table-wrapper">
                    <table className="inventario-table inventario-table-small">
                      <colgroup>
                        {columns.map((col) => (
                          <col
                            key={col.key}
                            style={{ width: `${columnWidths[col.key] || 120}px` }}
                          />
                        ))}
                      </colgroup>

                      <thead>
                        <tr>
                          {columns.map((col) => {
                            const isDocumento = col.key === "Documento";
                            const isCodigo = col.key === "Codigo";
                            const isDescripcion = col.key === "Descripcion";

                            let stickyClass = "";
                            let stickyStyle;

                            if (isDocumento) {
                              stickyClass = "inventario-sticky-col inventario-sticky-doc";
                              stickyStyle = { left: 0 };
                            } else if (isCodigo) {
                              stickyClass = "inventario-sticky-col inventario-sticky-col-1";
                              stickyStyle = { left: `${columnWidths.Documento || 110}px` };
                            } else if (isDescripcion) {
                              stickyClass = "inventario-sticky-col inventario-sticky-col-2";
                              stickyStyle = {
                                left: `${(columnWidths.Documento || 110) + (columnWidths.Codigo || 120)}px`,
                              };
                            }

                            return (
                              <th key={col.key} className={stickyClass} style={stickyStyle}>
                                <div className="inventario-th-inner">
                                  <span>{col.label}</span>
                                  <span
                                    className="inventario-col-resizer"
                                    onMouseDown={(e) => startResize(e, col.key)}
                                    title={`Cambiar ancho de ${col.label}`}
                                  />
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>

                      <tbody>
                        {modalLines.map((line, index) => (
                          <tr key={`${safeStr(line.Documento)}-${safeStr(line.Codigo)}-${index}`}>
                            <td
                              className="inventario-sticky-col inventario-sticky-doc"
                              style={{ left: 0 }}
                            >
                              {safeStr(line.Documento) || "N/D"}
                            </td>

                            <td
                              className={`inventario-sticky-col inventario-sticky-col-1 ${getCellHighlightClass(
                                line,
                                "Codigo"
                              )}`}
                              style={{ left: `${columnWidths.Documento || 110}px` }}
                            >
                              {safeStr(line.Codigo) || "N/D"}
                            </td>

                            <td
                              className="inventario-sticky-col inventario-sticky-col-2"
                              style={{
                                left: `${(columnWidths.Documento || 110) + (columnWidths.Codigo || 120)}px`,
                              }}
                            >
                              {safeStr(line.Descripcion) || "N/D"}
                            </td>

                            <td>{safeStr(line.Tag) || "N/D"}</td>
                            <td>{safeStr(line.Estante) || "N/D"}</td>
                            <td>{safeStr(line.Seccion) || "N/D"}</td>
                            <td>{safeStr(line.Nivel) || "N/D"}</td>

                            <td className={getCellHighlightClass(line, "Conteo")}>
                              {safeStr(line.Conteo) || "0"}
                            </td>

                            <td className={getCellHighlightClass(line, "stock_BCM01")}>
                              {safeStr(line.stock_BCM01) || "0"}
                            </td>
                            <td className={getCellHighlightClass(line, "stock_BCM02")}>
                              {safeStr(line.stock_BCM02) || "0"}
                            </td>
                            <td className={getCellHighlightClass(line, "stock_BCM03")}>
                              {safeStr(line.stock_BCM03) || "0"}
                            </td>
                            <td className={getCellHighlightClass(line, "stock_BCM04")}>
                              {safeStr(line.stock_BCM04) || "0"}
                            </td>
                            <td className={getCellHighlightClass(line, "stock_BPU01")}>
                              {safeStr(line.stock_BPU01) || "0"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {modalLines.length === 0 && (
                      <div className="inventario-empty-detail">
                        No hay líneas de detalle.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformeInventario;